<script lang="ts">
	import Dropzone from "dropzone"
	import "dropzone/dist/basic.css"

	import { LuxedoRPC } from "luxedo-rpc"
	import { onMount } from "svelte"

	interface Props {
		uploadMultiple?: boolean
		uploadUrl: any
		acceptedFiles?: any
		onSuccess?: (file: Dropzone.DropzoneFile) => void
		onFail?: (
			file: Dropzone.DropzoneFile,
			message: string | Error,
			xhr: XMLHttpRequest
		) => void
		onDropzoneSend?: (
			file: Dropzone.DropzoneFile,
			xhr: XMLHttpRequest,
			formData: FormData
		) => void
		/**
		 * Called when user presses upload button.
		 * Should handle upload and processing.
		 */
		onUpload: (
			files: Dropzone.DropzoneFile | Array<Dropzone.DropzoneFile>,
			name: string,
			dropzone: Dropzone
		) => Promise<void>
	}

	let {
		uploadMultiple = false,
		uploadUrl,
		acceptedFiles = [
			"image/jpeg",
			"image/png",
			"image/gif",
			"video/mp4",
			"video/webm",
			"video/quicktime",
			"audio/wav",
			"audio/mpeg",
		],
		onSuccess = () => {},
		onFail = () => {},
		onDropzoneSend = () => {},
		onUpload,
	}: Props = $props()

	let dz: Dropzone = $state()

	/**
	 * Called on file select - can selectively cancel or remove files here
	 */
	function onAccept(
		file: Dropzone.DropzoneFile,
		done: (error?: string | Error) => void
	) {
		fileList.push(file)
		canUpload = true
		if (fileList.length > 1) {
			if (!uploadMultiple) dz.removeFile(file)
			else {
				hideNameInput = true
			}
		} else {
			hideNameInput = false
		}
		done()
	}

	/**
	 * Called on file select after a thumbnail has been created
	 * TODO - apply thumbnail images
	 */
	function showThumbnail(file: Dropzone.DropzoneFile, dataUrl: string) {
		// console.log("tn", { file, dataUrl })
	}

	onMount(() => {
		// Create dropzone object
		dz = new Dropzone("#media-upload", {
			paramName: "file",
			maxFiles: 10,
			withCredentials: true,
			createImageThumbnails: true,
			acceptedFiles: acceptedFiles.join(","),
			url: `/${uploadUrl}`,
			headers: { "X-CSRF-TOKEN": LuxedoRPC.getCsrfToken() },
			autoProcessQueue: false,
			chunking: true,
			forceChunking: true,
			accept: onAccept,
			thumbnail: showThumbnail,
			sending: onDropzoneSend,
			error: onFail,
			success: onSuccess,
			processing: function () {
				this.options.autoProcessQueue = true
			},
			init: function () {},
		})
	})

	let fileList: Array<Dropzone.DropzoneFile> = []
	let canUpload: boolean = $state(false) // shows upload button
	let hideNameInput: boolean = $state(false) // used if user is uploading multiple files
	let fileName: string = $state()
</script>

{#if !uploadMultiple}
	<span
		class="input-container"
		style="visibility: {hideNameInput ? 'hidden' : 'visible'};"
	>
		<label for="media-title-input">Name</label>
		<input
			class="large"
			id="media-title-input"
			type="text"
			bind:value={fileName}
		/>
	</span>
{/if}

<form id="media-upload" class="dropzone"></form>

{#if canUpload}
	<button
		onclick={() =>
			onUpload(uploadMultiple ? fileList : fileList[0], fileName, dz)}
		>Upload</button
	>
{/if}

<style>
	form {
		width: 100%;
		margin-bottom: 1rem;
	}

	.dropzone {
		width: 100%;
		min-height: 12rem;
		max-width: 24rem;
		border-radius: var(--br);
		background-color: var(--color-bg-darker);
		display: flex;
		justify-content: center;
		align-items: flex-end;
		padding-bottom: 5rem;
		padding-top: 0.5rem;
	}

	.input-container {
		width: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
	}

	input {
		width: 100%;
		max-width: 24rem;
	}

	label {
		width: 100%;
		max-width: 24rem;
	}

	button {
		width: 100%;
		max-width: 24rem;
		text-align: center;
	}

	.dropzone :global(.dz-button) {
		transform: unset;
		color: var(--color-text-light);
		box-shadow: unset;
		position: absolute;
		bottom: 1rem;
		left: 0;
		right: 0;
		margin-right: auto;
		margin-left: auto;

		transition:
			color var(--transition-duration),
			background-color var(--transition-duration);
	}

	.dropzone :global(.dz-button:hover),
	.dropzone :global(.dz-button:focus-visible) {
		color: var(--color-text);
	}

	.dropzone :global(.dz-error-message) {
		background-color: var(--color-bg-dark);
		padding: 0.25rem;
		border-radius: var(--br);
		margin-top: 0.5rem;
		font-size: var(--text-small);
	}

	.dropzone :global(.dz-details) {
		font-size: var(--text-small);
		line-height: 1rem;
		padding-bottom: 0.25rem;
	}

	.dropzone :global(.dz-progress) {
		border-radius: var(--br);
		overflow: hidden;
		background-color: var(--color-bg);
	}

	.dropzone :global(.dz-file-preview) {
		background-color: var(--color-accent);
		border-radius: var(--br);
		padding: 0.5rem;
	}
</style>
